// EventListing.jsx — Farm selector + event table
// Create / Edit logic has been moved to EventFormModal.jsx
import { useState, useEffect, useRef } from "react";
import { getAllFarmsAdmin } from "../../../../../api/user-service/adminService";
import { getFarmById } from "../../../../../api/farm-service/farmService";
import { listByFarmId } from "../../../../../api/farm-service/experienceService";
import { getCategories } from "../../../../../api/farm-service/categoryService";
import EventFormModal from "./EventFormModal";
import "../../../../../assets/stylesheets/EventListing.css";
import DashboardLayout from "../../../DashboardLayout";

export default function EventListing() {
  /* ── Farm dropdown ────────────────────────────────────────────────── */
  const [farms, setFarms] = useState([]);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [farmSearch, setFarmSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  /* ── Category filter ──────────────────────────────────────────────── */
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [categoryPanelOpen, setCategoryPanelOpen] = useState(false);
  const categoryRef = useRef(null);

  /* ── Sort ─────────────────────────────────────────────────────────── */
  const [sortOrder] = useState("asc");

  /* ── Events table ─────────────────────────────────────────────────── */
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState("");

  /* ── Modal ────────────────────────────────────────────────────────── */
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [farmDetailLoading, setFarmDetailLoading] = useState(false);

  const [eventsRefreshKey, setEventsRefreshKey] = useState(0);

  /* ── Load all farms (paginated, with 503 retry) ──────────────────── */
  useEffect(() => {
    let cancelled = false;

    /** 503 Service Unavailable → Lambda cold start */
    async function fetchWithRetry(fn, retries = 3, delayMs = 1500) {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          const status = err?.response?.status;
          if (status === 503 && attempt < retries) {
            console.warn(`[EventListing] 503 retry ${attempt}/${retries} in ${delayMs}ms…`);
            await new Promise((r) => setTimeout(r, delayMs * attempt));
          } else {
            throw err;
          }
        }
      }
    }

    async function loadAllFarms() {
      setFarmsLoading(true);
      let page = 1;
      let accumulated = [];
      try {
        while (true) {
          const res = await fetchWithRetry(() =>
            getAllFarmsAdmin({ limit: 50, page }, { timeout: 30000 })
          );
          if (cancelled) return;
          const d1 = res?.data ?? res;
          const d2 = d1?.data ?? d1;
          const batch =
            Array.isArray(d2) ? d2 :
            Array.isArray(d2?.farms) ? d2.farms :
            Array.isArray(d2?.items) ? d2.items :
            Array.isArray(d2?.data) ? d2.data : [];
          if (batch.length === 0) break;
          const normalized = batch.map((f) => ({
            ...f,
            name: f.name ?? f.farmName ?? f.title ?? `Farm #${f.id}`,
          }));
          accumulated = [...accumulated, ...normalized];
          const seen = new Set();
          accumulated = accumulated.filter((f) => {
            if (seen.has(f.id)) return false;
            seen.add(f.id);
            return true;
          });
          setFarms([...accumulated]);
          if (batch.length < 50) break;
          page++;
        }
      } catch (err) {
        console.error("[EventListing] failed to load farms:", err);
      } finally {
        if (!cancelled) setFarmsLoading(false);
      }
    }
    loadAllFarms();
    return () => { cancelled = true; };
  }, []);

  /* ── Load categories ──────────────────────────────────────────────── */
  useEffect(() => {
    getCategories()
      .then((res) => {
        const data = res?.data ?? res;
        setCategories(Array.isArray(data) ? data : data?.categories ?? []);
      })
      .catch((err) => console.error("[EventListing] failed to load categories:", err));
  }, []);

  /* ── Outside-click: close category panel ─────────────────────────── */
  useEffect(() => {
    function handler(e) {
      if (categoryRef.current && !categoryRef.current.contains(e.target))
        setCategoryPanelOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Outside-click: close farm dropdown ──────────────────────────── */
  useEffect(() => {
    function handler(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setFarmSearch("");
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Load events when farm changes ───────────────────────────────── */
  useEffect(() => {
    if (!selectedFarm?.id) { setEvents([]); return; }
    setLoadingEvents(true);
    setEventsError("");
    const farmId = selectedFarm.id;
    // Admin 뷰 → draft 포함 전체 status 조회
    // 백엔드가 status 파라미터 없으면 published만 반환할 수 있으므로 명시적으로 전달
    listByFarmId(farmId, { status: "all" })
      .then((res) => {
        const d1 = res?.data ?? res;
        const d2 = d1?.data ?? d1;
        const list =
          Array.isArray(d2)              ? d2 :
          Array.isArray(d2?.experiences) ? d2.experiences :
          Array.isArray(d2?.items)       ? d2.items :
          Array.isArray(d2?.data)        ? d2.data :
          [];
        setEvents(list);
      })
      .catch((err) => {
        console.error("[EventListing] listByFarmId error:", err?.response?.status, err?.message);
        setEventsError("Failed to load events.");
      })
      .finally(() => setLoadingEvents(false));
  }, [selectedFarm?.id, eventsRefreshKey]);

  /* ── Filtered + sorted farm list ─────────────────────────────────── */
  const filteredFarms = (() => {
    let base = farms;
    if (selectedCategories.size > 0) {
      base = base.filter((f) => {
        const farmCats = Array.isArray(f.categories)
          ? f.categories.map((c) => (typeof c === "string" ? c : c?.name ?? c?.type ?? ""))
          : [];
        return farmCats.some((c) => selectedCategories.has(c));
      });
    }
    const q = farmSearch.toLowerCase().trim();
    let result;
    if (!q) {
      result = [...base];
    } else {
      const startsWith = [], contains = [];
      for (const f of base) {
        const n = (f.name ?? "").toLowerCase();
        if (n.startsWith(q)) startsWith.push(f);
        else if (n.includes(q)) contains.push(f);
      }
      result = [...startsWith, ...contains];
    }
    result.sort((a, b) => {
      const na = (a.name ?? "").toLowerCase();
      const nb = (b.name ?? "").toLowerCase();
      return sortOrder === "asc" ? na.localeCompare(nb) : nb.localeCompare(na);
    });
    return result;
  })();

  /* ── Handlers ─────────────────────────────────────────────────────── */
  function selectFarm(farm) {
    // Use basic farm info immediately, then enrich with full details (address, etc.)
    setSelectedFarm(farm);
    setDropdownOpen(false);
    setFarmSearch("");
    setEventsRefreshKey((k) => k + 1);
    setFarmDetailLoading(true);
    getFarmById(farm.id)
      .then((detail) => {
        // farmService.getFarmById already resolves res.data — detail IS the farm object
        // BFF might still wrap: { success, data: { farm } } or just { id, name, ... }
        const d = detail?.data ?? detail;
        setSelectedFarm((prev) => prev?.id === farm.id ? { ...prev, ...d, name: d?.name ?? prev.name } : prev);
      })
      .catch((err) => console.error("[EventListing] failed to load farm detail:", err))
      .finally(() => setFarmDetailLoading(false));
  }

  function toggleDropdown() {
    setDropdownOpen((v) => {
      if (!v) {
        setFarmSearch("");
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      return !v;
    });
  }

  function toggleCategory(name) {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function openCreate() {
    setEditingEvent(null);
    setModalOpen(true);
  }

  function openEdit(event) {
    setEditingEvent(event);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingEvent(null);
  }

  function handleCreated(created) {
    setEvents((prev) => [created, ...prev]);
  }

  function handleUpdated(updated) {
    setEvents((prev) => prev.map((ev) => (ev.id === updated.id ? updated : ev)));
  }

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <DashboardLayout>
      <div className="el-page">

        <div className="el-header">
          <h2 className="el-title">Event Listing</h2>
          {selectedFarm && (
            <button
              className="el-btn el-btn-primary"
              onClick={openCreate}
              disabled={farmDetailLoading}
            >
              {farmDetailLoading ? "Loading farm…" : "+ Create Event"}
            </button>
          )}
        </div>

        <div className="el-farm-row">
          <div className="el-farm-selector" ref={dropdownRef}>
            <button
              type="button"
              className={"el-farm-trigger" + (dropdownOpen ? " el-farm-trigger--open" : "")}
              onClick={toggleDropdown}
            >
              <span className={"el-farm-trigger-text" + (!selectedFarm ? " el-farm-trigger-placeholder" : "")}>
                {selectedFarm ? selectedFarm.name : farmsLoading ? "Loading farms…" : "Select farm…"}
              </span>
              <span className="el-farm-trigger-arrow">{dropdownOpen ? "▲" : "▼"}</span>
            </button>

            {dropdownOpen && (
              <div className="el-farm-panel">
                <div className="el-farm-panel-search">
                  <span className="el-farm-panel-search-icon">🔍</span>
                  <input
                    ref={searchInputRef}
                    className="el-farm-panel-input"
                    type="text"
                    placeholder="Search…"
                    value={farmSearch}
                    onChange={(e) => setFarmSearch(e.target.value)}
                  />
                </div>
                <ul className="el-farm-list">
                  {filteredFarms.length === 0 ? (
                    <li className="el-farm-list-empty">No farms found</li>
                  ) : (
                    filteredFarms.map((f) => (
                      <li
                        key={f.id}
                        className={"el-farm-list-item" + (selectedFarm?.id === f.id ? " el-farm-list-item--active" : "")}
                        onMouseDown={() => selectFarm(f)}
                      >
                        <span className="el-farm-list-name">{f.name}</span>
                        {selectedFarm?.id === f.id && <span className="el-farm-list-check">✓</span>}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="el-category-wrap" ref={categoryRef}>
            <button
              type="button"
              className={
                "el-filter-btn" +
                (categoryPanelOpen ? " el-filter-btn--open" : "") +
                (selectedCategories.size > 0 ? " el-filter-btn--active" : "")
              }
              onClick={() => setCategoryPanelOpen((v) => !v)}
              title="Filter by category"
            >
              <svg className="el-filter-icon" viewBox="0 0 24 24" fill="none">
                <line x1="3"  y1="6"  x2="21" y2="6"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="6"  y1="12" x2="18" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="10" y1="18" x2="14" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {selectedCategories.size > 0 && (
                <span className="el-category-badge">{selectedCategories.size}</span>
              )}
            </button>

            {categoryPanelOpen && (
              <div className="el-category-panel">
                <div className="el-category-panel-header">
                  <span>Filter by Category</span>
                  {selectedCategories.size > 0 && (
                    <button
                      type="button"
                      className="el-category-clear"
                      onClick={() => setSelectedCategories(new Set())}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <ul className="el-category-list">
                  {categories.length === 0 ? (
                    <li className="el-category-empty">No categories</li>
                  ) : (
                    categories.map((cat) => {
                      const name = typeof cat === "string"
                        ? cat
                        : cat?.name ?? cat?.type ?? String(cat?.id ?? "");
                      return (
                        <li key={name} className="el-category-item">
                          <label className="el-category-label">
                            <input
                              type="checkbox"
                              className="el-category-check"
                              checked={selectedCategories.has(name)}
                              onChange={() => toggleCategory(name)}
                            />
                            {name}
                          </label>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        {!selectedFarm ? (
          <p className="el-empty">Select a farm to view its events.</p>
        ) : loadingEvents ? (
          <p className="el-loading">Loading events…</p>
        ) : eventsError ? (
          <p className="el-error">{eventsError}</p>
        ) : events.length === 0 ? (
          <p className="el-empty">No events found for this farm.</p>
        ) : (
          <div className="el-table-wrap">
            <table className="el-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Season</th> 
                  <th>Status</th>
                  <th>Capacity</th>
                  <th>Duration (min)</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const duration = (ev.startAt && ev.endAt)
                    ? Math.round((new Date(ev.endAt) - new Date(ev.startAt)) / 60000)
                    : (ev.start_at && ev.end_at)
                    ? Math.round((new Date(ev.end_at) - new Date(ev.start_at)) / 60000)
                    : "-";

                  return (
                    <tr key={ev.id}>
                      <td>{ev.title}</td>
                      <td>{ev.season ?? "-"}</td>
                      <td>
                        <span className={"el-badge el-badge-" + (ev.status ?? "").toLowerCase()}>
                          {ev.status}
                        </span>
                      </td>
                      <td>{ev.capacity}</td>
                      <td>{duration}</td>
                      <td>{ev.currency} {Number(ev.price).toLocaleString()}</td>
                      <td>
                        <button
                          className="el-btn el-btn-sm el-btn-secondary"
                          onClick={() => openEdit(ev)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {modalOpen && (
          <EventFormModal
            farmId={selectedFarm?.id}
            farm={selectedFarm}
            event={editingEvent}
            onClose={closeModal}
            onCreated={handleCreated}
            onUpdated={handleUpdated}
          />
        )}

      </div>
    </DashboardLayout>
  );
}
